/**
 * Bug Review Modal Component
 *
 * Modal for reviewing bug reports and exporting to Linear
 */
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, CheckCircle2, Loader2 } from "lucide-react";
import { type BugReport, updateBugReportLinearExport } from "@/lib/instantdb";
import { toast } from "sonner";

// Linear API URL (proxied through our worker)
const LINEAR_API_URL =
  import.meta.env.VITE_LINEAR_API_URL || "https://linear.vizicloud.com";

interface Team {
  id: string;
  name: string;
  projects: { nodes: { id: string; name: string }[] };
  labels: { nodes: { id: string; name: string; color: string }[] };
}

interface BugReviewModalProps {
  bug: BugReport;
  onClose: () => void;
}

export function BugReviewModal({ bug, onClose }: BugReviewModalProps) {
  const [title, setTitle] = useState(bug.title);
  const [description, setDescription] = useState(bug.description);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
  const [priority, setPriority] = useState(2);
  const [isExporting, setIsExporting] = useState(false);
  const [isLoadingTeams, setIsLoadingTeams] = useState(true);
  const [viewingOriginal, setViewingOriginal] = useState(false);

  const selectedTeam = teams.find((t) => t.id === selectedTeamId);
  const isExported = !!bug.exportedAt;

  // Fetch Linear teams on mount
  useEffect(() => {
    async function fetchTeams() {
      try {
        const response = await fetch(`${LINEAR_API_URL}/teams`);
        if (!response.ok) throw new Error("Failed to fetch teams");
        const data = await response.json();
        setTeams(data.teams ?? []);
      } catch (error) {
        console.error("Failed to fetch Linear teams:", error);
        toast.error("Failed to load Linear teams");
      } finally {
        setIsLoadingTeams(false);
      }
    }

    if (!isExported) {
      fetchTeams();
    } else {
      setIsLoadingTeams(false);
    }
  }, [isExported]);

  const handleExport = async () => {
    if (!selectedTeamId) return;

    setIsExporting(true);
    try {
      // Build description with embedded screenshots
      const fullDescription = `
${description}

---

**Screenshots**

![Annotated Screenshot](${bug.annotatedScreenshotUrl})

<details>
<summary>Original Screenshot</summary>

![Original Screenshot](${bug.originalScreenshotUrl})

</details>

---

**Metadata**
- **Page URL:** ${bug.pageUrl}
- **Viewport:** ${bug.viewport}
- **User Agent:** ${bug.userAgent}
- **Reported:** ${new Date(bug.timestamp).toLocaleString()}
      `.trim();

      const response = await fetch(`${LINEAR_API_URL}/issues`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          teamId: selectedTeamId,
          title,
          description: fullDescription,
          priority,
          projectId: selectedProjectId || undefined,
          labelIds: selectedLabelIds.length > 0 ? selectedLabelIds : undefined,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to create Linear issue");
      }

      const result = await response.json();
      const { issue } = result;

      // Update InstantDB with Linear reference
      await updateBugReportLinearExport(bug.id, {
        linearIssueId: issue.id,
        linearIssueUrl: issue.url,
        linearIssueIdentifier: issue.identifier,
      });

      toast.success(`Created Linear issue: ${issue.identifier}`);
      onClose();
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export to Linear");
    } finally {
      setIsExporting(false);
    }
  };

  const toggleLabel = (labelId: string) => {
    setSelectedLabelIds((prev) =>
      prev.includes(labelId)
        ? prev.filter((id) => id !== labelId)
        : [...prev, labelId],
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Review Bug Report
            {isExported && (
              <Badge variant="default" className="gap-1">
                <CheckCircle2 className="h-3 w-3" />
                Exported
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-6">
          {/* Left: Screenshot */}
          <div className="space-y-4">
            <div className="overflow-hidden rounded-lg border bg-muted">
              <img
                src={
                  viewingOriginal
                    ? bug.originalScreenshotUrl
                    : bug.annotatedScreenshotUrl
                }
                alt="Screenshot"
                className="w-full"
              />
            </div>
            <div className="flex justify-center gap-2">
              <Button
                variant={!viewingOriginal ? "default" : "outline"}
                size="sm"
                onClick={() => setViewingOriginal(false)}
              >
                Annotated
              </Button>
              <Button
                variant={viewingOriginal ? "default" : "outline"}
                size="sm"
                onClick={() => setViewingOriginal(true)}
              >
                Original
              </Button>
            </div>

            {/* Metadata */}
            <div className="space-y-1 text-sm text-muted-foreground">
              <p>
                <strong>Page:</strong> {bug.pageUrl}
              </p>
              <p>
                <strong>Viewport:</strong> {bug.viewport}
              </p>
              <p>
                <strong>Reported:</strong>{" "}
                {new Date(bug.timestamp).toLocaleString()}
              </p>
            </div>
          </div>

          {/* Right: Edit form */}
          <div className="space-y-4">
            {isExported ? (
              // Already exported view
              <div className="space-y-4">
                <div>
                  <Label>Title</Label>
                  <p className="mt-1">{bug.title}</p>
                </div>
                <div>
                  <Label>Description</Label>
                  <p className="mt-1 whitespace-pre-wrap">{bug.description}</p>
                </div>
                <div className="border-t pt-4">
                  <a
                    href={bug.linearIssueUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-primary hover:underline"
                  >
                    View in Linear: {bug.linearIssueIdentifier}
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>
            ) : (
              // Edit form
              <>
                <div className="space-y-2">
                  <Label htmlFor="issue-title">Issue Title</Label>
                  <Input
                    id="issue-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Refine the title for Linear"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="issue-description">Description</Label>
                  <Textarea
                    id="issue-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={6}
                    placeholder="Edit the description..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Team</Label>
                    <Select
                      value={selectedTeamId}
                      onValueChange={setSelectedTeamId}
                      disabled={isLoadingTeams}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            isLoadingTeams ? "Loading..." : "Select team"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {teams.map((team) => (
                          <SelectItem key={team.id} value={team.id}>
                            {team.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Project (optional)</Label>
                    <Select
                      value={selectedProjectId}
                      onValueChange={setSelectedProjectId}
                      disabled={!selectedTeam}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">None</SelectItem>
                        {selectedTeam?.projects.nodes.map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select
                    value={String(priority)}
                    onValueChange={(v) => setPriority(Number(v))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">No priority</SelectItem>
                      <SelectItem value="1">Urgent</SelectItem>
                      <SelectItem value="2">High</SelectItem>
                      <SelectItem value="3">Medium</SelectItem>
                      <SelectItem value="4">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {selectedTeam && selectedTeam.labels.nodes.length > 0 && (
                  <div className="space-y-2">
                    <Label>Labels</Label>
                    <div className="flex flex-wrap gap-2">
                      {selectedTeam.labels.nodes.map((label) => (
                        <Badge
                          key={label.id}
                          variant={
                            selectedLabelIds.includes(label.id)
                              ? "default"
                              : "outline"
                          }
                          className="cursor-pointer"
                          style={{
                            backgroundColor: selectedLabelIds.includes(label.id)
                              ? `#${label.color}`
                              : undefined,
                            borderColor: `#${label.color}`,
                          }}
                          onClick={() => toggleLabel(label.id)}
                        >
                          {label.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleExport}
                    disabled={!selectedTeamId || !title || isExporting}
                  >
                    {isExporting && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {isExporting ? "Exporting..." : "Export to Linear"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
